<template>
  <!-- #region template -->
  <b-container fluid class="table m-0">
    <b-row>
      <!-- #region header -->
      <b-col cols="12" class="mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex justify-content-between align-items-center" style="gap: 1.5rem">
            <!-- #region title -->
            <h5 class="font-weight-medium m-0 text-dark">
              {{ options.title }}
            </h5>
            <!-- #endregion -->
            <!-- #region filters -->
            <template v-if="options.filters">
              <b-dropdown menu-class="text-nowrap" variant="link" toggle-class="text-decoration-none p-0" :disabled="busy" v-for="(filter, index) in options.filters" :key="index">
                <template #button-content>
                  <span>{{ filter.label }}</span>
                </template>
                <div class="p-3">
                  <b-form-checkbox-group @change="update" v-model="filter.selected" :options="filter.options" value-field="item" text-field="name" stacked disabled-field="notEnabled"></b-form-checkbox-group>
                </div>
              </b-dropdown>
              <b-button class="border" variant="light" v-on:click="reset" :disabled="busy">Reset filters</b-button>
            </template>
            <!-- #endregion -->
          </div>
          <div class="d-flex justify-content-between align-items-stretch" style="gap: 1rem">
            <!-- #region actions -->
            <b-dropdown class="border rounded" size="lg" style="height: 36px; width: 36px" variant="link" right toggle-class="text-decoration-none p-0 d-flex justify-content-center align-items-center" :disabled="busy" no-caret>
              <template #button-content>
                <b-icon icon="download" style="height: 14px; width: 16px"></b-icon>
              </template>
              <b-dropdown-item v-on:click="handle(TableAction.Exporting, collectionSnapshot)" href="#">Export</b-dropdown-item>
              <!-- <b-dropdown-item v-on:click="handle(TableAction.Importing, [])" href="#">Import</b-dropdown-item> -->
            </b-dropdown>
            <!-- #endregion -->
            <!-- #region add -->
            <b-button
              variant="primary"
              :disabled="busy"
              v-on:click="
                if (options.actions.add) {
                  handle(TableAction.Adding, [])
                }
              "
              >New {{ pluralToSingular(options.title).toLowerCase() }}</b-button
            >
            <!-- #endregion -->
          </div>
        </div>
      </b-col>
      <!-- #endregion -->
      <!-- #region table -->
      <b-col cols="12">
        <b-table class="bg-white border m-0" ref="table" show-empty hover :items="collectionSnapshot.docs" :per-page="options.perPage" :fields="options.fields" :busy="busy" @row-clicked="myRowClickHandler">
          <!-- #region empty -->
          <template #empty>
            <div class="text-center my-2">
              <p>You don't have any {{ options.title.toString() }} yet. Try adding one by clicking on the "New {{ pluralToSingular(options.title).toLowerCase() }}" button above.</p>
            </div>
          </template>
          <!-- #endregion -->
          <!-- #region busy -->
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mr-2"></b-spinner>
            </div>
          </template>
          <!-- #endregion -->
          <!-- #region colgroup -->
          <template #table-colgroup="scope">
            <col v-for="field in scope.fields" :key="field.key" :style="{ width: field.width || 'auto' }" />
          </template>
          <!-- #endregion -->
          <!-- #region head -->
          <template #head()="data">
            <p class="font-weight-medium m-0">{{ data.label }}</p>
          </template>
          <!-- #endregion -->
          <!-- #region cell -->
          <template #cell()="cell">
            <!-- #region status -->
            <template v-if="cell.field.type == 'status'">
              <p class="m-0" :class="{ 'text-success': cell.value, 'text-warning': !cell.value }">{{ cell.value ? 'Published' : 'Draft' }}</p>
            </template>
            <!-- #endregion -->
            <!-- #region timestamp -->
            <template v-else-if="cell.field.type == 'timestamp'">
              {{ timestampToDate(cell.value) }}
            </template>
            <!-- #endregion -->
            <!-- #region country -->
            <template v-else-if="cell.field.type == 'country'">
              {{ getCountryByCode(cell.value) }}
            </template>
            <!-- #endregion -->
            <!-- #region cardinal -->
            <template v-else-if="cell.field.type == 'cardinal'">
              {{ GetCardinals(cell.value) }}
            </template>
            <!-- #endregion -->
            <!-- #region multiselect -->
            <!-- //TODO what is this refKey? -->
            <template v-else-if="cell.field.type == 'multiselect'">
              {{ concatArrayValueByKey(cell.value, cell.field.refKey) }}
            </template>
            <!-- #endregion -->
            <!-- #region select -->
            <template v-else-if="cell.field.type == 'select'"> {{ getSelectValue(cell.field.key, cell.value) }} </template>
            <!-- #endregion -->
            <!-- #region number -->
            <template v-else-if="cell.field.type == 'number'"> {{ formatToCurrency(cell.value, Currency.EURO) }} </template>
            <!-- #endregion -->
            <!-- #region boolean -->
            <template v-else-if="cell.field.type == 'boolean'">
              <p :style="{ color: cell.value ? 'green' : 'orange' }">{{ cell.value ? cell.field.set : cell.field.unset }}</p>
            </template>
            <!-- #endregion -->
            <!-- #region image -->
            <template v-else-if="cell.field.type == 'image'">
              <div class="d-flex justify-content-center">
                <div style="position: relative; height: 38px; width: 38px; background-color: #f0f0f0; border-radius: 4px" class="border d-flex justify-content-center align-items-center">
                  <img :src="getCellValue(cell)" style="height: 100%; max-width: 100%" />
                </div>
              </div>
            </template>
            <!-- #endregion -->
            <!-- #region url -->
            <template v-else-if="cell.field.type == 'url'">
              <a target="_blank" :href="getCellValue(cell)">{{ getCellValue(cell) }}</a>
            </template>
            <!-- #endregion -->
            <!-- #region progress -->
            <template v-else-if="cell.field.type == 'progress'">
              <template v-for="(obj, index) in [getProgress(cell.item)]">
                <b-progress b-progress style="max-width: 300px" :value="obj.value" :variant="obj.variant" height="0.5rem" :key="index"></b-progress>
              </template>
            </template>
            <!-- #endregion -->
            <!-- #region actions -->
            <template v-else-if="cell.field.type == 'actions'">
              <b-dropdown size="lg" variant="link" right toggle-class="text-decoration-none p-0" no-caret>
                <template #button-content>
                  <b-icon class="text-muted" icon="three-dots-vertical" aria-hidden="true"></b-icon>
                </template>
                <!-- <template v-if="options.actions.print">
                  <b-dropdown-item size="sm" @click="handle(TableAction.Printing, [cell.item])" class="mr-1">Print</b-dropdown-item>
                </template> -->
                <template v-if="options.actions.call">
                  <b-dropdown-item :href="'tel:' + cell.item.phone" size="sm" class="mr-1">Call</b-dropdown-item>
                </template>
                <template v-if="options.actions.email">
                  <b-dropdown-item :href="'mailto:' + cell.item.email" size="sm" class="mr-1">Mail</b-dropdown-item>
                </template>
              </b-dropdown>
            </template>
            <!-- #endregion -->
            <template v-else>
              {{ getEllipsis(getCellValue(cell)) }}
            </template>
          </template>
          <!-- #endregion -->
          <!-- #region caption -->
          <template #table-caption>
            <div class="w-100">
              <div class="d-flex justify-content-end" style="gap: 1rem">
                <b-button class="border rounded" :disabled="!collectionSnapshot.previous" v-on:click="previous">
                  <b-icon icon="chevron-left" style="height: 14px; width: 16px"></b-icon>
                </b-button>
                <b-button class="border rounded" :disabled="!collectionSnapshot.next" v-on:click="next">
                  <b-icon icon="chevron-right" style="height: 14px; width: 16px"></b-icon>
                </b-button>
              </div>
            </div>
          </template>
          <!-- #endregion -->
        </b-table>
      </b-col>
      <!-- #endregion -->
      <!-- #region modals -->
      <!-- #region add -->
      <b-modal hide-footer ref="add-modal" size="xl" :title="'Add ' + pluralToSingular(options.title).toLowerCase()" body-class="p-5">
        <cForm
          @close="
            $refs['add-modal'].toggle()
            update()
          "
          type="add"
          :blueprint="options.blueprint"
          :init="documents[0]"
          :documents="collectionSnapshot.docs" />
      </b-modal>
      <!-- #endregion -->
      <!-- #region edit -->
      <b-modal hide-footer ref="edit-modal" size="xl" :title="'Edit ' + pluralToSingular(options.title).toLowerCase()" body-class="p-5">
        <cForm
          @close="
            $refs['edit-modal'].toggle()
            update()
          "
          @deleteEntry="$refs['remove-modal'].toggle()"
          type="edit"
          :blueprint="options.blueprint"
          :init="documents[0]"
          :documents="collectionSnapshot.docs" />
      </b-modal>
      <!-- #endregion -->
      <!-- #region remove -->
      <b-modal ref="remove-modal" header-bg-variant="danger" header-text-variant="light" :title="'Delete ' + pluralToSingular(options.title).toLowerCase()">
        <div>
          <p>You are about to delete the following documents from the collection:</p>
          <b-table :items="documents" :fields="[options.fields[0]]"></b-table>
          <p>Are you sure you want to continue? This action is irreversible.</p>
        </div>
        <template #modal-footer>
          <b-button variant="secondary" @click="$refs['remove-modal'].toggle()" :disabled="busy">Cancel</b-button>
          <b-button variant="danger" v-on:click="deleting" :disabled="busy">Delete</b-button>
        </template>
      </b-modal>

      <!-- #endregion -->
      <!-- #region import -->
      <b-modal hide-footer ref="import-modal" size="xl" :title="'Import ' + options.title">
        <div>
          <p>You can import data to your collection here. The supported formats are JSON and CSV.</p>
          <b-form-group>
            <b-form-file @change="parsing($event)"></b-form-file>
          </b-form-group>
          <b-table :items="documents" :fields="options.fields"></b-table>
          <b-button variant="primary" v-on:click="importing" :disabled="busy">Import</b-button>
        </div>
      </b-modal>

      <!-- #endregion -->
      <!-- #region export -->
      <b-modal hide-footer ref="export-modal" size="xl" :title="'Export ' + options.title">
        <div>
          <p v-if="!busy">You can export your data to from your collection here. The supported formats are JSON and CSV.</p>
          <p v-else>Please wait, it can take up to several minutes.</p>
          <p>
            <b-button variant="primary" :disabled="busy" v-on:click="exporting(ExportingType.JSON)">
              <template v-if="!busy"> Download JSON </template>
              <template v-else> <b-spinner small type="grow"></b-spinner> Please wait... </template>
            </b-button>
            <b-button variant="primary" class="ml-2" :disabled="busy" v-on:click="exporting(ExportingType.CSV)">
              <template v-if="!busy"> Download CSV </template>
              <template v-else> <b-spinner small type="grow"></b-spinner> Please wait... </template>
            </b-button>
            <!-- <b-button variant="primary" class="ml-2" :disabled="busy" v-on:click="exporting(ExportingType.PRINT)">Print</b-button> -->
            <b-button variant="primary" class="ml-2" :disabled="busy" v-on:click="exporting(ExportingType.TEMPLATE)">
              <template v-if="!busy"> Download Template </template>
              <template v-else> <b-spinner small type="grow"></b-spinner> Please wait... </template>
            </b-button>
          </p>
        </div>
      </b-modal>
      <!-- #endregion -->
      <!-- #endregion -->
    </b-row>
  </b-container>
  <!-- #endregion -->
</template>

<script>
// #region script
// #region import
import cForm from '@/components/cForm'
import { mapState, mapActions } from 'vuex'
import { pluralToSingular, ExportingType, TableAction, timestampToDate, getCountryByCode, concatArrayValueByKey, getEllipsis, toPRINT, formatToCurrency, Currency, GetCardinals, byString, documentsToJSON, documentToCSV } from '@/utils'
// #endregion

// #region export
export default {
  name: 'wTable',
  components: {
    cForm,
  },
  props: {
    options: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      busy: true,
      documents: [],
      collectionSnapshot: {},
      // #region utils
      ExportingType,
      TableAction,
      Currency,
      // #endregion
    }
  },
  computed: {
    ...mapState(['settings']),
  },
  created() {
    this.options.filters?.forEach((filter) => {
      this['firestore/getDocs']({
        path: filter.collection.name,
      }).then((documents) => {
        filter.options = []
        filter.selected = []
        documents.forEach((value) => {
          filter.options.push({
            item: value._reference,
            name: value.name,
          })
        })
        this.update()
      })
    })
    if (!this.options.filters) {
      this.update()
    }
  },
  methods: {
    // #region mapActions
    ...mapActions(['firestore/deleteDocs', 'firestore/populateDoc', 'populateDocument', 'firestore/getDocs', 'firestore/populateDoc', 'firestore/getDoc', 'firestore/collection']),
    // #endregion
    // #region utils
    pluralToSingular,
    timestampToDate,
    getCountryByCode,
    concatArrayValueByKey,
    getEllipsis,
    toPRINT,
    formatToCurrency,
    GetCardinals,
    byString,
    // #endregion
    next() {
      if (this.collectionSnapshot.next) {
        this.busy = true
        this.collectionSnapshot.next().then(async (collectionSnapshot) => {
          this.collectionSnapshot = await this.populate(collectionSnapshot)
          this.busy = false
        })
      }
    },
    previous() {
      if (this.collectionSnapshot.previous) {
        this.busy = true
        this.collectionSnapshot.previous().then(async (collectionSnapshot) => {
          this.collectionSnapshot = await this.populate(collectionSnapshot)
          this.busy = false
        })
      }
    },
    // the first load of the webpage (gets the first database page)
    async update() {
      this.busy = true
      let filters = []
      for (let i = 0; i < this.options.filters?.length; i++) {
        if (this.options.filters[i].selected.length) {
          filters.push({
            fieldPath: this.options.filters[i].key,
            opStr: this.options.filters[i].opStr,
            value: this.options.filters[i].selected,
          })
        }
      }
      await this['firestore/collection']({
        settings: {
          orderBy: '_onCreateDate',
          filters,
          limit: 16,
        },
        path: this.options.blueprint.collection,
      }).then(async (collectionSnapshot) => {
        this.collectionSnapshot = await this.populate(collectionSnapshot)
        this.busy = false
      })
    },
    // TODO - this populate function does not support the value of a key in the object, being an array of references. (ie: categories on products)
    async populate(collectionSnapshot) {
      for (let i = 0; i < collectionSnapshot.docs.length; i++) {
        for (let j = 0; j < this.options.fields.length; j++) {
          if (this.options.fields[j].reference) {
            let segments = this.options.fields[j].key.split('.')
            let path = []
            let ref = collectionSnapshot.docs[i]
            for (let k = 0; k < segments.length - 1; k++) {
              path.push(segments[k])
              if (this.byString(collectionSnapshot.docs[i], path.join('.')).path) {
                ref[segments[k]] = await this['firestore/getDoc']({
                  path: this.byString(collectionSnapshot.docs[i], path.join('.')).path,
                })
                ref = ref[segments[k]]
              }
            }
          }
        }
      }
      return collectionSnapshot
    },
    //reset the view, and updates
    reset() {
      this.update()
    },
    //handles an action on the table
    handle(action, documents) {
      switch (action) {
        case TableAction.Adding:
          this.documents = [{}]
          this.options.blueprint.form.tabs.forEach((tab) => {
            tab.elements.forEach((element) => {
              this.documents[0][element.key] = element.default
            })
          })
          this.$refs['add-modal'].toggle()
          break
        case TableAction.Editing:
          this.documents = documents
          this.$refs['edit-modal'].toggle()
          break
        case TableAction.Copying:
          this.documents = {
            ...documents,
          }
          delete this.documents[0].reference
          this.$refs['add-modal'].toggle()
          break
        case TableAction.Deleting:
          this.documents = documents
          this.$refs['remove-modal'].toggle()
          break
        case TableAction.Importing:
          this.documents = documents
          this.$refs['import-modal'].toggle()
          break
        case TableAction.Exporting:
          this.documents = documents
          this.$refs['export-modal'].toggle()
          break
        case TableAction.Printing:
          this.busy = true
          let document = { ...documents[0] }
          this['firestore/getDoc']({
            path: document.configuration.path,
          })
            .then((documentSnapshot) => {
              document.configuration = documentSnapshot
              return this['firestore/getDoc']({
                path: document.configuration.product.path,
              })
            })
            .then((documentSnapshot) => {
              document.configuration.product = documentSnapshot
              return this['firestore/getDoc']({
                path: document.dealer.path,
              })
            })
            .then((documentSnapshot) => {
              document.dealer = documentSnapshot
              let values = []
              document.configuration.options.forEach((option, i) => {
                values.push(
                  this['firestore/getDoc']({
                    path: option.path,
                  }).then((documentSnapshot) => {
                    document.configuration.options[i] = documentSnapshot
                  })
                )
              })
              return Promise.all(values)
            })
            .then(() => {
              this.busy = false
              toPRINT(document, this.options)
            })

          break
        default:
          break
      }
    },
    //parsing documents for import
    parsing() {},
    //importing documents
    importing() {},
    //exporting documents
    // enum ExportingType
    async exporting(exportType) {
      this.busy = true
      this['firestore/getDocs']({
        path: this.options.blueprint.collection,
      })
        .then(async (documents) => {
          let collectionSnapshot = {
            docs: documents,
          }
          collectionSnapshot = await this.populate(collectionSnapshot)
          let docs = []
          collectionSnapshot.docs.forEach((doc) => {
            let document = {}
            this.options.fields.forEach((field) => {
              if (field.key != 'actions') {
                if (field.reference) {
                  document[field.label] = this.byString(doc, field.key)
                } else if (field.type == 'timestamp') {
                  document[field.label] = this.timestampToDate(doc[field.key])
                } else if (field.type == 'boolean') {
                  document[field.label] = doc[field.key] ? field.set : field.unset
                } else if (field.type == 'country') {
                  document[field.label] = this.getCountryByCode(doc[field.key])
                } else {
                  document[field.label] = doc[field.key]
                }
              }
            })
            docs.push(document)
          })
          switch (exportType) {
            case ExportingType.JSON:
              documentsToJSON(docs, this.options.blueprint)
              break
            case ExportingType.CSV:
              documentToCSV(docs, this.options.blueprint)
              break
            default:
              break
          }

          this.busy = false
        })
        .finally(() => {})
    },
    //deleting the selected documents
    deleting() {
      this['firestore/deleteDocs']({
        paths: this.documents.map((document) => {
          return document._reference.path
        }),
      })
        .then(() => {
          this.$refs['remove-modal'].toggle()
          this.$refs['edit-modal'].toggle()
          this.update()
        })
        .catch(() => {
          // TODO Task: Error Handling (firebase error) - Comment: Create an error handling view, that expects props, and render it here
        })
    },
    myRowClickHandler(record) {
      if (this.options.actions.edit) {
        this.handle(TableAction.Editing, [record])
      } else {
        this.$emit('openDocument', record) //TODO this is for requests/orders/lead, but is hardcoded..
      }
      /*
        this.$emit("openView", record, this.options.blueprint.view)
      */
    },
    getSelectValue(key, value) {
      loop: for (let i = 0; i < this.options.blueprint.form.tabs.length; i++) {
        let tab = this.options.blueprint.form.tabs[i]
        console.log('tab', tab)
        for (let i = 0; i < tab.elements.length; i++) {
          let element = tab.elements[i]
          console.log('element', element)
          if (element.key == key) {
            for (let i = 0; i < element.options.length; i++) {
              let option = element.options[i]
              console.log('option', option)
              if (option.value == value) {
                value = option.text
                break loop
              }
            }
          }
        }
      }
      return value
    },
    // #region table cells
    getCellValue(cell) {
      return [cell.field.prefix, cell.value.toString(), cell.field.suffix].filter(Boolean).join('')
    },
    getProgress(document) {
      let x = 0
      this.options.blueprint.form.tabs.forEach((tab) => {
        x += tab.elements.length
      })
      let y = 0
      this.options.blueprint.form.tabs.forEach((tab) => {
        tab.elements.forEach((element) => {
          if (document[element.key] != undefined) {
            y++
          }
        })
      })
      return {
        value: (y / x) * 100,
        variant: (y / x) * 100 == 100 ? 'success' : (y / x) * 100 >= 50 ? 'warning' : 'danger',
      }
    },
    // #endregion
  },
}
// #endregion
// #endregion
</script>

<style lang="scss">
// #region style

// #endregion
</style>
